/* ============================================================================
   Esquema canónico del dataset.

   Principio rector: NINGÚN número existe sin procedencia. Cada punto de cada
   serie carga el id de su fuente y su nivel de confianza, y si fue estimado se
   marca como tal para que la interfaz nunca lo presente como observado.

   Este archivo es la única definición del contrato entre el pipeline de ingesta
   y la aplicación.
   ========================================================================== */

/** 4 — añade el precio base para compra (`retail`). */
export const SCHEMA_VERSION = 4

/** Periodo mensual `YYYY-MM`. */
export type Period = string

/**
 * Confianza en el dato:
 *   high   — tomado de la publicación oficial primaria y verificado.
 *   medium — fuente oficial secundaria, o derivado de una oficial con un cálculo
 *            documentado.
 *   low    — reportado por terceros, o pendiente de verificación.
 */
export type Confidence = 'high' | 'medium' | 'low'

/** Unidades. El tipo obliga a declararla: nunca «pesos» a secas. */
export type Unit =
  /** Pesos por persona al mes (canastas de CONEVAL / líneas de pobreza). */
  | 'MXN_persona_mes'
  /** Pesos por hogar al mes. */
  | 'MXN_hogar_mes'
  /** Pesos por hogar a la semana. */
  | 'MXN_hogar_semana'
  /** Pesos por día (salario mínimo). */
  | 'MXN_dia'
  /** Pesos por unidad de producto (kg, litro, pieza). */
  | 'MXN_unidad'
  /** Índice con base declarada en la serie. */
  | 'indice'
  /** Porcentaje. */
  | 'porcentaje'
  /** Conteo de canastas (cociente). */
  | 'canastas'
  /** Personas. */
  | 'personas'
  /** Toneladas. */
  | 'toneladas'

export const UNIT_LABEL: Record<Unit, string> = {
  MXN_persona_mes: 'pesos por persona al mes',
  MXN_hogar_mes: 'pesos por hogar al mes',
  MXN_hogar_semana: 'pesos por hogar a la semana',
  MXN_dia: 'pesos por día',
  MXN_unidad: 'pesos por unidad',
  indice: 'índice',
  porcentaje: 'por ciento',
  canastas: 'canastas',
  personas: 'personas',
  toneladas: 'toneladas',
}

/** Ámbito geográfico. `nacional` agrega rural + urbano. */
export type Geography = string

/** Una fuente de datos, citada íntegra. */
export interface SourceRef {
  id: string
  /** Nombre del producto de información, no de la dependencia. */
  name: string
  institution: string
  /** URL de la página o archivo específico consultado. */
  url: string
  /** URL de la nota metodológica, si existe. */
  methodologyUrl?: string
  /**
   * Cadencia de publicación declarada por la fuente.
   *
   * `quincenal` entró con Profeco QQP: sus metadatos declaran «Mensual» pero el
   * archivo trae dos entregas por mes (`05-2026_Q1`, `05-2026_Q2`) y la vista
   * rotula la quincena de corte, así que el tipo tiene que poder expresarla.
   *
   * Es la cadencia DECLARADA, no la observada. Profeco regenera el año completo
   * por lotes irregulares —al 31 de julio de 2026 lo más nuevo era la segunda
   * quincena de mayo, 63 días atrás—, así que la antigüedad se publica siempre
   * desde `max(fecha_registro)` y jamás desde este campo ni desde `retrievedAt`.
   */
  cadence: 'mensual' | 'bimestral' | 'trimestral' | 'semestral' | 'anual' | 'quincenal' | 'semanal' | 'diaria' | 'irregular'
  /** Fecha ISO en que el pipeline consultó la fuente. */
  retrievedAt: string
  /** Licencia o términos declarados. */
  license?: string
  /** Advertencias operativas: cambios de metodología, cortes de serie, etc. */
  caveats?: string[]
  /** `false` si el endpoint no pudo verificarse en la última corrida. */
  verified: boolean
}

/** Un punto observado de una serie mensual. */
export interface SeriesPoint {
  period: Period
  value: number
  sourceId: string
  confidence: Confidence
  /**
   * `true` sólo si el valor NO viene de la fuente: se derivó por cálculo,
   * interpolación o empalme. La interfaz lo dibuja distinto y lo dice.
   */
  derived?: boolean
  /** Cómo se derivó, cuando `derived` es `true`. */
  derivation?: string
}

export interface Series {
  id: string
  /** Nombre completo, para tooltips y tablas. */
  label: string
  /** Nombre corto, para leyendas y ejes. */
  shortLabel: string
  unit: Unit
  geography: Geography
  /** Canasta a la que pertenece, si aplica. */
  basketId?: string
  sourceId: string
  /** Base del índice, p. ej. «segunda quincena de julio de 2018 = 100». */
  indexBase?: string
  /** Si más alto es peor (precios) o mejor (poder de compra). */
  higherIsBetter: boolean
  points: SeriesPoint[]
}

/** Definición de una canasta. Coexisten varias y no son comparables entre sí. */
export interface Basket {
  id: string
  name: string
  institution: string
  /** Para qué sirve y qué mide, en una frase. */
  description: string
  /** Número de productos, si la canasta lo define. */
  productCount?: number
  unit: Unit
  sourceId: string
  /** La canasta principal de la app. Exactamente una debe tener `true`. */
  primary: boolean
  /** Notas de interpretación que deben mostrarse junto a la cifra. */
  caveats?: string[]
}

export type ProductCategory =
  | 'cereales'
  | 'carnes'
  | 'pescados'
  | 'lacteos-huevo'
  | 'frutas'
  | 'verduras'
  | 'leguminosas'
  | 'azucares-grasas'
  | 'bebidas'
  | 'otros'

export const CATEGORY_LABEL: Record<ProductCategory, string> = {
  cereales: 'Cereales',
  carnes: 'Carnes',
  pescados: 'Pescados y mariscos',
  'lacteos-huevo': 'Lácteos y huevo',
  frutas: 'Frutas',
  verduras: 'Verduras',
  leguminosas: 'Leguminosas',
  'azucares-grasas': 'Azúcares y grasas',
  bebidas: 'Bebidas',
  /* Es la categoría más grande de la canasta urbana, así que no puede llamarse
     «Otros»: son alimentos preparados, sobre todo consumidos fuera de casa. */
  otros: 'Preparados y fuera de casa',
}

export interface Product {
  id: string
  name: string
  category: ProductCategory
  /** Unidad de venta: `kg`, `litro`, `pieza`, `600 g`… */
  unit: string
  /** Canastas oficiales que lo incluyen. */
  basketIds: string[]
  /**
   * Gramos por persona al mes POR ÁMBITO, con las mismas claves que
   * `ProductSeries.geography` («rural», «urbano»).
   *
   * Tiene que ser por ámbito: la canasta rural y la urbana asignan cantidades
   * distintas al mismo renglón. Guardarlo como un solo número hacía que la app
   * mostrara la cantidad rural bajo el rótulo «ámbito urbano» —hasta 7 veces la
   * real— y se contradijera con la lista de la despensa.
   */
  gramsPerPersonMonth?: Record<string, number>
}

/**
 * Qué mide una serie de producto:
 *   precio        — pesos por kilogramo, litro o ración, tal como los publica la
 *                   fuente. Es lo que se compara entre meses y entre productos.
 *   valor-mensual — pesos al mes por persona que ese renglón aporta a la canasta.
 *                   Suma de los renglones = valor de la canasta. Sirve para el
 *                   desglose y para la calculadora de despensa, no para comparar
 *                   precios entre productos.
 */
export type ProductSeriesKind = 'precio' | 'valor-mensual'

/** Serie mensual de un producto, en una geografía y para una medida. */
export interface ProductSeries {
  productId: string
  kind: ProductSeriesKind
  geography: Geography
  unit: Unit
  sourceId: string
  points: SeriesPoint[]
}

/* --- Territorio ----------------------------------------------------------
   El costo de la canasta se publica sólo para el país, en ámbito rural y urbano.
   Lo que sí existe con detalle territorial es el ÍNDICE de precios: cuánto
   subieron los alimentos en cada entidad y en cada una de las 55 ciudades de la
   muestra del INPC.

   Un índice mide cambio, no nivel. Dos lugares con índices distintos no tienen
   precios distintos: subieron distinto desde la fecha base. El esquema lo hace
   explícito con `indexBase` y `PLACE_INDEX_WARNING`, y la app tiene prohibido
   convertir estos índices a pesos por lugar.
   ------------------------------------------------------------------------ */

export type PlaceKind = 'nacional' | 'entidad' | 'ciudad'

export const PLACE_KIND_LABEL: Record<PlaceKind, string> = {
  nacional: 'Nacional',
  entidad: 'Entidad federativa',
  ciudad: 'Ciudad',
}

/** Advertencia obligatoria junto a cualquier comparación entre lugares. */
export const PLACE_INDEX_WARNING =
  'Un índice mide cuánto cambiaron los precios, no cuánto cuestan. Comparar dos ' +
  'lugares dice dónde subieron más desde la fecha base, nunca dónde es más caro.'

export interface Place {
  id: string
  name: string
  kind: PlaceKind
  /**
   * Clave INEGI de entidad (`01`–`32`). Las ciudades la traen para poder
   * agruparlas por estado. Es `null` en la Zona Metropolitana de la Ciudad de
   * México, que abarca dos entidades.
   */
  stateCode: string | null
  /** Nombre de la entidad a la que pertenece una ciudad. */
  stateName?: string
}

/** Qué mide la serie territorial. */
export type PlaceSeriesKind = 'alimentos' | 'general'

export const PLACE_SERIES_LABEL: Record<PlaceSeriesKind, string> = {
  alimentos: 'Alimentos, bebidas y tabaco',
  general: 'Índice general',
}

export interface PlaceSeries {
  placeId: string
  kind: PlaceSeriesKind
  unit: Unit
  /** Base declarada. Todas las series territoriales comparten la misma. */
  indexBase: string
  sourceId: string
  points: SeriesPoint[]
}

/* --- Abasto mayorista -----------------------------------------------------
   De dónde puede salir el alimento, no cuánto cuesta. Sale del DENUE del INEGI,
   el directorio de unidades económicas del país.

   Aquí SÓLO viven agregados, y no por privacidad: por peso. El directorio
   completo —los 23 654 establecimientos, uno por uno— también se publica, pero
   en `public/mayoristas.json`, que la app baja sólo al abrir Abasto.

   Lo que NO se publica en ninguno de los dos es el contacto. El DENUE trae
   correo o teléfono de dos tercios de los establecimientos, y el 91 % de esos
   correos son de una persona, no un buzón de empresa: meterlos al JSON de un
   sitio estático los volvería raspables de un tirón. La lista con contactos la
   escribe el pipeline en `data/contactos/`, que no entra a `dist/` ni a git,
   para que se use con un proceso de consentimiento y no como página web.
   ------------------------------------------------------------------------ */

export interface WholesaleClass {
  /** Clave SCIAN de seis dígitos. */
  code: string
  label: string
  short: string
}

export interface WholesaleState {
  stateCode: string
  stateName: string
  total: number
  /**
   * Establecimientos de 11 personas ocupadas en adelante.
   *
   * NO es una medida de capacidad: el DENUE no publica ventas. Es un filtro de
   * primera pasada para separar la bodega familiar del proveedor que
   * plausiblemente surte volumen con factura.
   */
  volume: number
  /** Dentro de una central de abasto. */
  inCentral: number
  /** Conteo por clave SCIAN. */
  byClass: Record<string, number>
}

/** Plaza de abasto, agregada por municipio. Ver `inegi-denue.ts` para el porqué. */
export interface SupplyHub {
  id: string
  stateCode: string
  stateName: string
  municipality: string
  /** Nombres de central tal como los registra el DENUE, del más al menos común. */
  markets: string[]
  total: number
  volume: number
  byClass: Record<string, number>
}

export interface WholesaleDirectory {
  sourceId: string
  /** Edición del DENUE que declara el propio archivo, p. ej. «05_2026». */
  edition: string
  /** Fecha de última modificación que declara el INEGI. */
  editionDate: string
  classes: WholesaleClass[]
  states: WholesaleState[]
  hubs: SupplyHub[]
  total: number
  volumeTotal: number
  inCentralTotal: number
  municipalities: number
  /** Cuántos traen contacto en el DENUE. Se cuenta; los datos NO se publican. */
  withContact: number
}

/* --- Precio base para compra (Profeco QQP) ---------------------------------
   Esta sección NO mide el costo de la canasta y su forma lo impone: aquí no
   existe ningún campo que se llame «canasta», no existe ningún total en pesos
   de una lista de compra, y ninguna cifra se escala a un hogar. Lo único que
   se publica es el precio de UN producto, en UNA presentación, en UN canal,
   con la nube de la que salió.

   Existe para una pregunta: BAMX compra a nivel nacional y reparte a sus 64
   bancos; para convocar una compra necesita un precio de referencia que pueda
   defender en un acta. Ese precio sale de la mediana en central de abasto, y
   su credibilidad depende de tres cosas que aquí van SIEMPRE pegadas al
   número: cuántas observaciones lo sostienen, en cuántas entidades, y qué tan
   ancha es la nube.

   El límite duro: contra el boletín del INEGI esta fuente se desvía en mediana
   más de 20 % por renglón y sólo 10 de 40 renglones caen dentro de ±10 %.
   Sirve para fijar un precio de compra y para comparar canales entre sí. NO es
   una segunda estimación del costo de la canasta y el sitio no la resta ni la
   promedia con la del INEGI en ningún lado.
   ------------------------------------------------------------------------ */

/** Canal de venta, derivado de `giro`. NUNCA de `cadena_comercial`. */
export type RetailChannel = 'central' | 'autoservicio' | 'mercado' | 'tortilleria'

export const RETAIL_CHANNEL_LABEL: Record<RetailChannel, string> = {
  central: 'Central de abasto',
  autoservicio: 'Autoservicio',
  mercado: 'Mercado público',
  tortilleria: 'Tortillería',
}

/** Confianza del mapeo renglón→QQP. `baja` EXISTE en el tipo y significa una
 *  cosa concreta: el renglón no entra al precio base. No hay etiqueta amarilla
 *  que rescate un factor de unidad inventado. */
export type RetailConfidence = 'alta' | 'media' | 'baja'

/** Presentación de referencia. Declarada a mano en el catálogo del pipeline y
 *  re-derivada en cada corrida para que un cambio de mezcla de Profeco falle
 *  en vez de deslizarse (guarda G7b). */
export interface RetailReference {
  /** `a137-granel-negro`. Declarado; jamás derivado del texto de QQP. */
  id: string
  /** Clave cruda del boletín, en mayúsculas: `A137`. Sin prefijo, igual que
   *  `enighKey` en `inegi-bulletins.ts`. El renglón de `products` que le
   *  corresponde es `enigh-${claveEnigh.toLowerCase()}` (`ingest.ts:1004`):
   *  guardar ya el id compuesto rompería el cruce contra el catálogo ENIGH. */
  claveEnigh: string
  /** «Frijol negro, granel, 1 kg». Prosa nuestra. */
  label: string
  /** La cadena literal de `presentacion` de Profeco, para poder auditarla.
   *  Ojo: los grados son presentaciones DISTINTAS en QQP
   *  («…Saladette/huaje» vs «…Saladette/huaje. Primera»). La referencia es la
   *  cadena exacta, no un prefijo. */
  presentacionQqp: string
  /** `granel` habilita el precio base por tonelada; `empaque` no. */
  forma: 'granel' | 'empaque'
  /** false cuando «mismo producto + misma presentación» NO garantiza misma
   *  calidad: granel de cárnicos, charcutería, quesos y pescado. Con false, el
   *  margen contra anaquel NO se publica aunque haya pareo geográfico. */
  paridadCalidad: boolean
  confidence: Exclude<RetailConfidence, 'baja'>   // 'baja' nunca llega aquí
  unit: Unit                 // 'MXN_unidad'
  denominador: 'kg' | 'L'    // se imprime junto a cada cifra
}

/** Una celda de precio. Cinco cuantiles, nunca un promedio.
 *
 *  La celda del jitomate saladette sin grado en central, mayo de 2026, son 53
 *  observaciones que van de $28.79 (p10) a $52.80 (p90) con mediana $40.00. Ese
 *  ancho no es ruido que promediar: es el mercado. Una media lo colapsa a una
 *  cifra que ninguna bodega cotiza, y quien fije un precio base con ella se
 *  entera del rango cuando le llegan las ofertas.
 *
 *  Ojo con el ejemplo tentador y equivocado: el mismo día, en la misma central,
 *  «jitomate a $42, $34 y $20» son las calidades Primera, Segunda y Tercera, que
 *  en QQP son PRESENTACIONES DISTINTAS y por lo tanto celdas distintas. Citarlo
 *  como dispersión dentro de una celda invita a empatar la presentación por
 *  prefijo, que es justo lo que R2 prohíbe: con prefijo, esta celda pasa de
 *  n=53 y $40.00 a n=411 y $35.00, y deja de describir un bien comprable. */
export interface PriceCell {
  refId: string
  channel: RetailChannel
  /** `MX` o clave de entidad de dos dígitos. Nunca municipio. */
  geography: string
  period: Period
  n: number
  /** Entidades con al menos una observación en la celda. */
  states: number
  /** Puntos de venta distintos por lat/lon, no por nombre_comercial. */
  outlets: number
  p10: number; p25: number; p50: number; p75: number; p90: number
  /** IC 95 % de la mediana por estadísticos de orden. `null` si n < 8. */
  ciLo: number | null
  ciHi: number | null
}

/** `publicable` = la celda pasó las tres condiciones de la compuerta R4
 *  (n ≥ 12 ∧ (p75−p25)/p50 ≤ 0.40 ∧ entidades ≥ 3). En mayo de 2026 pasan 29 de
 *  35: `solo-rango` no es el caso raro y la vista no lo trata como excepción. */
export type BandGrade = 'publicable' | 'solo-rango'

/** Veredicto del margen contra anaquel. `sin-diferencia` NO es empate: es
 *  «con esta muestra no se puede afirmar una diferencia». Medido: 10 de los 14
 *  renglones con margen calculable caen aquí. */
export type MarginVerdict =
  | 'anaquel-mas-caro'
  | 'central-mas-cara'
  | 'sin-diferencia'
  | 'sin-paridad-de-canal'    // la presentación no existe en anaquel
  | 'sin-paridad-de-calidad'  // existe, pero no es el mismo bien
  | 'sin-pareo-suficiente'    // menos de 5 entidades con los dos lados

export interface RetailMargin {
  refId: string
  period: Period
  verdict: MarginVerdict
  /**
   * Mediana de los márgenes POR ENTIDAD, **en puntos porcentuales ya escalados**:
   * la tortilla vale `-41.43`, no `-0.4143`. Se imprime con `percentDelta()`,
   * que sólo le pega el signo y el « %».
   *
   * La unidad va escrita porque la ambigüedad ya costó: leyendo el dataset con
   * un `×100` de más, la tortilla salía «−4143 %» y el número es lo bastante
   * absurdo para notarse, pero el del agua —«2077 %» contra 20.77 %— no lo es.
   * `null` salvo en los tres veredictos con número, y nunca 0: un margen de cero
   * es `sin-diferencia`, que es una afirmación distinta.
   */
  pct: number | null
  /** Extremos del IC 95 %, en las mismas unidades que `pct`. */
  ciLo: number | null
  ciHi: number | null
  /** Entidades pareadas que lo sostienen. */
  states: number
  /** Prosa que explica el veredicto cuando no hay número. Obligatoria. */
  reason: string | null
}

export interface PriceBand {
  refId: string
  period: Period
  geography: string
  central: PriceCell | null
  anaquel: PriceCell | null
  grade: BandGrade
  /** Qué umbral falló, en prosa, cuando `grade === 'solo-rango'`. */
  gradeReason: string | null
  margin: RetailMargin
  /** %/mes por mínimos cuadrados sobre ln(p50) de la serie mensual en central. */
  derivaMensualPct: number | null
  /** R² de esa recta. Bajo 0.5 la deriva NO se presenta como tendencia. */
  derivaR2: number | null
}

/** Ranking de cadenas dentro de una plaza, con caso completo. */
export interface RetailChainQuote {
  chainId: string
  /** Mediana de medianas estatales sobre `refIds`. Cada entidad pesa uno: una
   *  mediana agrupada le regalaría los precios del Valle de México a quien
   *  tenga más tiendas ahí, y el Valle es el 36 % de las filas de QQP. */
  p50: number
  n: number
  states: number
}

export interface RetailChainRanking {
  refId: string
  period: Period
  /** Entidades donde TODAS las cadenas del ranking cotizan la referencia.
   *  Medido para las cuatro grandes: 19. */
  commonStates: string[]
  /** Caso completo: si una cadena no cotiza la referencia en la plaza, la
   *  plaza sale del ranking de TODAS. Una cadena no puede salir barata por no
   *  vender algo. */
  chains: RetailChainQuote[]
  /**
   * La central de abasto medida con LA MISMA regla que las cadenas: mediana de
   * sus medianas estatales, restringida a `commonStates`. Existe porque la
   * vista pintaba de ancla el p50 nacional de la central —mediana agrupada
   * sobre sus 11 entidades— junto a filas calculadas sobre 16-19 entidades
   * comunes, y las dos bases no se comparan. En la papa el nacional da $38.00 y
   * la misma regla sobre las comunes da $39.50: no es un ajuste cosmético, es
   * que Chedraui pasa de empatar el ancla a leerse por debajo. Medido sobre los
   * 21 rankings que la vista pinta en mayo de 2026, el ancla se mueve entre
   * −30.0 % y +25.0 %, y en 6 de ellos alguna cadena cambia de lado.
   *
   * `n` es cuántas medianas estatales entraron y NO es `states`: la central
   * existe en 11 de las 32 entidades y casi nunca cotiza en todas las comunes.
   * Medido en el mismo corte, `n` va de 1 a 7 contra las 16-19 de cada cadena.
   * Por eso `n` viaja: sin él, una mediana de una entidad se lee como si
   * tuviera la misma base que las demás, que es el error que este campo vino a
   * corregir.
   *
   * `null` cuando la central no cotiza la presentación en ninguna entidad
   * común. No es cero ni se sustituye por el nacional: es que sobre la base del
   * ranking esta cifra no existe.
   */
  central: RetailChainQuote | null
}

export interface RetailComparator {
  sourceId: string
  edition: string            // '2026', leída del propio archivo
  lastQuincena: string       // '05-2026_Q2'
  lastObservation: string    // max(fecha_registro), ISO
  ageDays: number            // calculado de lastObservation, no de la corrida
  periods: Period[]
  references: RetailReference[]
  /** Bandas del ÚLTIMO mes, nacional. El resto vive en el JSON aparte. */
  bands: PriceBand[]
  rankings: RetailChainRanking[]
  /** 12 puntos por (refId, canal) para la chispa. Sin desglose geográfico. */
  series: { refId: string; channel: RetailChannel; points: SeriesPoint[] }[]
  /** El contraste contra el INEGI, RECALCULADO cada corrida. Los conteos
   *  literales en prosa se pudren; éstos no se teclean en ninguna vista. */
  contrasteInegi: {
    period: Period
    renglones: number
    desviacionMedianaPct: number
    dentroDe10: number
  }
  cobertura: { quincenas: string[]; faltantes: string[] }
  detailFile: string         // 'precio-base-2026.json'
  detailRows: number
}

/**
 * Orden de los campos de cada celda de `public/precio-base-2026.json`.
 *
 * Las filas van como arreglos y no como objetos por la misma razón y con el
 * mismo patrón que `DIRECTORY_FIELDS` (`inegi-denue.ts`): son ~75 000 celdas y
 * repetir las doce llaves en cada una cuesta ~1.4 MB de puras comillas.
 *
 * Lo que NO va en el archivo: `direccion`, `latitud`, `longitud` ni
 * `nombre_comercial`. No por privacidad —son negocios— sino porque publicar el
 * precio de una tienda identificable convierte un agregado en una acusación, y
 * Profeco no documenta su muestreo. `outlets` es un conteo, no una lista.
 */
export const RETAIL_FIELDS = ['refId','canal','geo','periodo','n','states','outlets','p10','p25','p50','p75','p90'] as const

/**
 * Orden de los campos de las filas de cadena del mismo archivo.
 *
 * Llevan `cadena` porque el ranking sí mete la marca a la llave y el precio
 * base no: en «Frijol · Bolsa 900 Gr. Negro», misma ciudad y misma quincena,
 * Aurrera cotiza $22.22/kg y Verde Valle $69.44/kg — 3.1×. Sin marca, el
 * ranking compara catálogos, no cadenas.
 *
 * Van tres cuantiles y no cinco: la fila es (cadena, entidad, mes) con caso
 * completo, y a esa granularidad p10 y p90 son uno o dos precios sueltos con
 * nombre de estadístico. La mediana y el rango intercuartílico bastan para
 * ordenar cadenas.
 */
export const RETAIL_CHAIN_FIELDS = ['refId','cadena','geo','periodo','n','p25','p50','p75'] as const

/** Advertencia obligatoria junto a cualquier cifra de precio de tienda.
 *  Vive en el esquema, no en la vista, porque es parte del contrato del dato
 *  —mismo lugar y misma razón que PLACE_INDEX_WARNING. */
export const RETAIL_NOT_CANASTA_WARNING =
  'Los precios de esta sección salen de Profeco, que levanta precios de anaquel ' +
  'y de mayoreo en una muestra de establecimientos. El costo de la canasta sale ' +
  'del INEGI, con otro diseño muestral y otro universo de comercios. Miden cosas ' +
  'distintas: sirven para fijar un precio de compra y comparar canales, no como ' +
  'segunda estimación del costo de la canasta. No se restan ni se promedian.'

/** Advertencia obligatoria junto a cualquier ranking de cadenas. */
export const RETAIL_OVERLAP_WARNING =
  'Dos cadenas sólo se comparan sobre la misma presentación, en las entidades ' +
  'donde las dos la venden. Cuando una cadena no cotiza el producto en una ' +
  'entidad, esa entidad sale del cálculo de todas: si no, una cadena saldría ' +
  'barata por no vender algo.'

/** Nota editorial atada a un periodo o a una serie. */
export interface DataNote {
  id: string
  kind: 'metodologia' | 'corte-de-serie' | 'advertencia' | 'contexto'
  title: string
  body: string
  /** Series o canastas afectadas. */
  appliesTo?: string[]
  period?: Period
  sourceUrl?: string
}

/** Hecho de contexto con cita (población, carencia alimentaria, cifras BAMX). */
export interface ContextFact {
  id: string
  label: string
  value: number
  unit: Unit
  displayValue: string
  asOf: string
  sourceId: string
  confidence: Confidence
}

export interface Coverage {
  firstPeriod: Period
  lastPeriod: Period
  /** Periodos esperados sin dato, para que la app no finja continuidad. */
  gaps: Period[]
}

export interface Dataset {
  schemaVersion: number
  /** ISO del momento en que se generó el archivo. */
  generatedAt: string
  /** Último periodo con dato en la serie principal. */
  latestPeriod: Period
  coverage: Coverage
  sources: SourceRef[]
  baskets: Basket[]
  series: Series[]
  products: Product[]
  productSeries: ProductSeries[]
  places: Place[]
  placeSeries: PlaceSeries[]
  /** Abasto mayorista. `null` si la fuente falló en la última corrida. */
  wholesale: WholesaleDirectory | null
  /** Precio base para compra. `null` si la fuente falló en la última corrida. */
  retail: RetailComparator | null
  contextFacts: ContextFact[]
  notes: DataNote[]
  /** Resultado de la última corrida del pipeline, para el panel de estado. */
  ingestion: IngestionReport
}

export interface IngestionReport {
  runAt: string
  results: IngestionResult[]
}

export interface IngestionResult {
  sourceId: string
  status: 'ok' | 'sin-cambios' | 'error' | 'omitido'
  message: string
  /** Cuántos puntos se escribieron desde esta fuente. */
  pointsWritten: number
  /** Último periodo obtenido de esta fuente. */
  latestPeriod?: Period
}

/* --- Helpers de lectura (usados por la app) ------------------------------ */

export function findSeries(dataset: Dataset, id: string): Series | undefined {
  return dataset.series.find((s) => s.id === id)
}

export function requireSeries(dataset: Dataset, id: string): Series {
  const s = findSeries(dataset, id)
  if (!s) throw new Error(`Serie no encontrada en el dataset: ${id}`)
  return s
}

export function findSource(dataset: Dataset, id: string): SourceRef | undefined {
  return dataset.sources.find((s) => s.id === id)
}

export function primaryBasket(dataset: Dataset): Basket {
  const b = dataset.baskets.find((x) => x.primary)
  if (!b) throw new Error('El dataset no declara una canasta principal')
  return b
}

export function pointAt(series: Series, period: Period): SeriesPoint | undefined {
  return series.points.find((p) => p.period === period)
}

export function lastPoint(series: Series): SeriesPoint | undefined {
  return series.points.length > 0 ? series.points[series.points.length - 1] : undefined
}
